import { useRef } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CennikFacety } from "@/api/schemas";
import { formatPocet } from "@/lib/format";
import { formatKategoria } from "@/lib/kategorie";
import { cn } from "@/lib/utils";
import {
  ANY,
  type CennikSidebarFiltre,
  type DruhFilter,
  type MaterialovyTab,
  type Zoradenie,
} from "@/components/cennik/cennikZalozky";
import { kategorieMoznosti, type Moznost } from "@/components/cennik/CennikSidebar";
import {
  DalsieFiltre,
  POHLADY_UPOZORNENI,
  dalsieFiltreZalozky,
} from "@/components/cennik/UpozorneniaCennika";
import { ZoraditPodlaZmeny } from "@/components/cennik/ZmenaCenyZnak";
import {
  FILTER_TLACIDLO,
  FILTER_ZAPNUTY,
  KategoriaVyberu,
} from "@/components/vyber/KategoriaVyberu";

/**
 * Riadok filtrov nad tabuľkou Cenníka (KOLO 199, okno B): hľadanie
 * a tlačidlá-rozbaľovačky v jednom riadku, pod ním zapnuté filtre ako
 * kapsule s ✕. Dovtedy boli rozbaľovačky v bočnom paneli 240 px, ktorý
 * na mobile ostal vedľa tabuľky a stlačil ju.
 *
 * Každá rozbaľovačka ráta s ostatnými filtrami, nie so sebou — po výbere
 * dodávateľa ukáže kategórie len jeho, dodávateľov stále všetkých (fasety,
 * KOLO 166).
 */

const DRUHY: Record<Exclude<DruhFilter, "vsetky">, string> = {
  plosny: "plošné",
  dlzkovy: "dĺžkové",
};

type Props = {
  tab: MaterialovyTab;
  /** Počty z `GET /cennik/facety`; počas prvého načítania `undefined`. */
  facety: CennikFacety | undefined;
  filtre: CennikSidebarFiltre;
  onZmena: (zmena: Partial<CennikSidebarFiltre>) => void;
  hladat: string;
  onHladat: (text: string) => void;
  zoradenie: Zoradenie | null;
  onZoradenie: (z: Zoradenie | null) => void;
  /** Zruší filtre aj hľadanie. */
  onZrusitVsetko: () => void;
};

export function CennikFiltre({
  tab,
  facety,
  filtre,
  onZmena,
  hladat,
  onHladat,
  zoradenie,
  onZoradenie,
  onZrusitVsetko,
}: Props): JSX.Element {
  const vyrobcaLabel = tab === "hrany" ? "Kolekcia" : "Výrobca";

  const druhy = new Map(facety?.druhy.map((d) => [d.hodnota, d.pocet]) ?? []);
  const pocetDruhu = (d: string): number | undefined =>
    facety ? (druhy.get(d) ?? 0) : undefined;

  const dodavatelia = facety?.dodavatelia ?? [];
  // Mená zapamätané naprieč záložkami a druhmi — vybraný dodávateľ, ktorý
  // fasety ešte nevrátili, má ostať čitateľný („JafHolz", nie „jaf")
  const menaDodavatelov = useRef(new Map<string, string>());
  for (const d of dodavatelia) menaDodavatelov.current.set(d.hodnota, d.nazov);
  const menoDodavatela = (kod: string): string => menaDodavatelov.current.get(kod) ?? kod;
  const vyrobcovia = facety?.vyrobcovia ?? [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[14rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Vyhľadať (ID, názov, dekor)…"
            value={hladat}
            onChange={(e) => onHladat(e.target.value)}
            className="h-9 pl-9"
            aria-label="Vyhľadať v cenníku"
          />
        </div>

        {tab === "materialy" ? (
          <Select
            value={filtre.druhFilter}
            onValueChange={(v) => onZmena({ druhFilter: v as DruhFilter })}
          >
            <SelectTrigger
              aria-label="Filter druhu"
              className={cn(FILTER_TLACIDLO, filtre.druhFilter !== "vsetky" && FILTER_ZAPNUTY)}
            >
              <SelectValue>
                {filtre.druhFilter === "vsetky" ? "Druh" : `Druh: ${DRUHY[filtre.druhFilter]}`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vsetky">
                Všetky (plošné + dĺžkové)
                <Pocet n={facety ? facety.druhy.reduce((s, d) => s + d.pocet, 0) : undefined} />
              </SelectItem>
              <SelectItem value="plosny">
                Plošné (dosky)
                <Pocet n={pocetDruhu("plosny")} />
              </SelectItem>
              <SelectItem value="dlzkovy">
                Dĺžkové (PD, zásteny…)
                <Pocet n={pocetDruhu("dlzkovy")} />
              </SelectItem>
            </SelectContent>
          </Select>
        ) : null}

        <KategoriaVyberu
          bloky={facety ? kategorieMoznosti(facety, tab === "kovanie") : undefined}
          hodnota={filtre.kategoria}
          onVyber={(h) => onZmena({ kategoria: h })}
        />

        <Select value={filtre.dodavatel} onValueChange={(v) => onZmena({ dodavatel: v })}>
          <SelectTrigger
            aria-label="Filter dodávateľa"
            className={cn(FILTER_TLACIDLO, filtre.dodavatel !== ANY && FILTER_ZAPNUTY)}
          >
            <SelectValue>
              {filtre.dodavatel === ANY
                ? "Dodávateľ"
                : `Dodávateľ: ${menoDodavatela(filtre.dodavatel)}`}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Všetci dodávatelia</SelectItem>
            {/* Vybraný dodávateľ, ktorého fasety (ešte) nevrátili,
                ostane čitateľný — inak by bol výber prázdny */}
            {filtre.dodavatel !== ANY &&
            !dodavatelia.some((d) => d.hodnota === filtre.dodavatel) ? (
              <SelectItem value={filtre.dodavatel}>{menoDodavatela(filtre.dodavatel)}</SelectItem>
            ) : null}
            {dodavatelia.map((d) => (
              <MoznostItem
                key={d.hodnota}
                moznost={{ hodnota: d.hodnota, label: d.nazov, pocet: d.pocet }}
              />
            ))}
          </SelectContent>
        </Select>

        <Select value={filtre.vyrobca} onValueChange={(v) => onZmena({ vyrobca: v })}>
          <SelectTrigger
            aria-label={`Filter ${vyrobcaLabel.toLowerCase()}`}
            className={cn(FILTER_TLACIDLO, filtre.vyrobca !== ANY && FILTER_ZAPNUTY)}
          >
            <SelectValue>
              {filtre.vyrobca === ANY ? vyrobcaLabel : `${vyrobcaLabel}: ${filtre.vyrobca}`}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Všetci</SelectItem>
            {filtre.vyrobca !== ANY &&
            !vyrobcovia.some((v) => v.hodnota === filtre.vyrobca) ? (
              <SelectItem value={filtre.vyrobca}>{filtre.vyrobca}</SelectItem>
            ) : null}
            {vyrobcovia.map((v) => (
              <MoznostItem
                key={v.hodnota}
                moznost={{ hodnota: v.hodnota, label: v.nazov, pocet: v.pocet }}
              />
            ))}
          </SelectContent>
        </Select>

        <ZoraditPodlaZmeny zoradenie={zoradenie} onZmena={onZoradenie} />
        <DalsieFiltre tab={tab} facety={facety} filtre={filtre} onZmena={onZmena} />
      </div>

      <ZapnuteFiltre
        tab={tab}
        filtre={filtre}
        hladat={hladat}
        vyrobcaLabel={vyrobcaLabel}
        menoDodavatela={menoDodavatela}
        onZmena={onZmena}
        onZrusitVsetko={onZrusitVsetko}
      />
    </div>
  );
}

/** Počet v zátvorke za pevným textom, kým fasety nie sú, nič. */
function Pocet({ n }: { n: number | undefined }): JSX.Element | null {
  if (n === undefined) return null;
  return <span className="ml-1 text-muted-foreground">({formatPocet(n)})</span>;
}

/** Možnosť s počtom; vybraná hodnota bez riadkov je sivá. */
function MoznostItem({ moznost }: { moznost: Moznost }): JSX.Element {
  return (
    <SelectItem
      value={moznost.hodnota}
      className={cn(moznost.pocet === 0 && "text-muted-foreground")}
    >
      {moznost.label}
      <span className="ml-1 text-muted-foreground">({formatPocet(moznost.pocet)})</span>
    </SelectItem>
  );
}

/** Zapnuté filtre ako kapsule s ✕ a „Zrušiť všetko" — len keď niečo je. */
function ZapnuteFiltre({
  tab,
  filtre,
  hladat,
  vyrobcaLabel,
  menoDodavatela,
  onZmena,
  onZrusitVsetko,
}: {
  tab: MaterialovyTab;
  filtre: CennikSidebarFiltre;
  hladat: string;
  vyrobcaLabel: string;
  menoDodavatela: (kod: string) => string;
  onZmena: (zmena: Partial<CennikSidebarFiltre>) => void;
  onZrusitVsetko: () => void;
}): JSX.Element | null {
  const kapsule: { kluc: string; text: string; zrus: Partial<CennikSidebarFiltre> }[] = [];
  if (filtre.druhFilter !== "vsetky") {
    kapsule.push({
      kluc: "druh",
      text: `Druh: ${DRUHY[filtre.druhFilter]}`,
      zrus: { druhFilter: "vsetky" },
    });
  }
  if (filtre.kategoria !== ANY) {
    kapsule.push({
      kluc: "kategoria",
      text: `Kategória: ${formatKategoria(filtre.kategoria)}`,
      zrus: { kategoria: ANY },
    });
  }
  if (filtre.dodavatel !== ANY) {
    kapsule.push({
      kluc: "dodavatel",
      text: `Dodávateľ: ${menoDodavatela(filtre.dodavatel)}`,
      zrus: { dodavatel: ANY },
    });
  }
  if (filtre.vyrobca !== ANY) {
    kapsule.push({
      kluc: "vyrobca",
      text: `${vyrobcaLabel}: ${filtre.vyrobca}`,
      zrus: { vyrobca: ANY },
    });
  }
  for (const p of POHLADY_UPOZORNENI) {
    if (filtre[p.kluc]) kapsule.push({ kluc: p.kluc, text: p.label, zrus: { [p.kluc]: false } });
  }
  for (const d of dalsieFiltreZalozky(tab)) {
    if (filtre[d.kluc]) kapsule.push({ kluc: d.kluc, text: d.kratko, zrus: { [d.kluc]: false } });
  }
  if (kapsule.length === 0 && !hladat) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Zapnuté filtre">
      {kapsule.map((k) => (
        <button
          key={k.kluc}
          type="button"
          aria-label={`Zrušiť filter ${k.text}`}
          title="Klik filter zruší"
          onClick={() => onZmena(k.zrus)}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary/40 bg-tandem-red-light px-2.5 py-0.5 text-xs font-medium text-tandem-red-dark hover:border-primary"
        >
          <span className="truncate">{k.text}</span>
          <X className="h-3 w-3 shrink-0" aria-hidden />
        </button>
      ))}
      <button
        type="button"
        onClick={onZrusitVsetko}
        className="px-1.5 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        Zrušiť všetko
      </button>
    </div>
  );
}
